import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { MapApi } from '../../../api/map-api';
import { Interceptor } from '../../../classes/interceptor';
import { IMCMap, MCMapConstructor } from '../../../classes/mcmap';
import { IMCMapView, MCMapViewConstructor } from '../../../classes/mcMapView';
import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter, JSONConverter } from '../../../inputConverter';
import { AoiService } from '../../../services/aoi.service';
import { ComponentService } from '../../../services/component.service';
import { DeepLinkProcessor } from '../../../services/deep-link-processor.service';
import { DeepLinkService } from '../../../services/deep-link.service';
import { DocumentService } from '../../../services/document.service';
import { IUserSettingsService } from '../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { NearbyService } from '../../../services/nearby.service';
import { QueryStringParamService } from '../../../services/query-string-param-service';
import { ScalebarService } from '../../../services/scalebar.service';
import { SpatialQueryService } from '../../../services/spatialQuery.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { HeatmapMode, ILayerLoadStatusChangeDetail, IMapcoreFeatureLayer, IMapcoreSubLayer, ISearchCriteria, MapCoreDocument } from '../../../types';
import { BaseComponent } from '../../base.component';
import { BasemapId, CreateBasemapsFromConfig, IBasemapConfig } from '../basemap-switcher/basemap-config';

let MapConstructor: MCMapConstructor;
let MapViewConstructor: MCMapViewConstructor;

const defaultLatitude = -76.9743399;
const defaultLongitude = 40.2684045;
const defaultZoom = 11;

/** Map Component - Displays an ESRI map */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-map',
    styleUrls: ['../../sharedStyles/base.scss', './map.component.scss'],
    templateUrl: './map.component.html'
})
export class MapComponent extends BaseComponent implements AfterViewInit {
    @ViewChild('mapArea') private readonly mapAreaRef: ElementRef<HTMLDivElement>;

    // Slots
    @ViewChild('bottomLeft') private readonly slotBottomLeft: ElementRef<HTMLSlotElement>;
    @ViewChild('bottomRight') private readonly slotBottomRight: ElementRef<HTMLSlotElement>;
    @ViewChild('topLeft') private readonly slotTopLeft: ElementRef<HTMLSlotElement>;
    @ViewChild('topRight') private readonly slotTopRight: ElementRef<HTMLSlotElement>;

    @Input() defaultComponents: DefaultMapComponent[] = [];

    @Input()
    @InputConverter(JSONConverter)
    center: number[] = [defaultLatitude, defaultLongitude];

    @Input()
    get basemap() {
        if (this.map && this.map.basemap) {
            return this.map.basemap.id;
        } else {
            return this._basemap;
        }
    }
    set basemap(value) {
        this._basemap = value;
        if (this.mapView && this.mapView.map) {
            this.mapView.map.set('basemap', this._basemap);
        }
    }
    private _basemap: string | __esri.BasemapProperties | IBasemapConfig | BasemapId = 'streets-vector';

    @Input()
    get rotate() { return this._rotate; }
    set rotate(value: boolean) {
        this._rotate = BooleanConverter(value);
        if (this.mapView && this.mapView.constraints) {
            this.mapView.constraints.rotationEnabled = this._rotate;
        }
    }
    private _rotate = true;

    @Input()
    get enableClickEvents() { return this._enableClickEvents; }
    set enableClickEvents(value: boolean) {
        this._enableClickEvents = BooleanConverter(value);
    }
    private _enableClickEvents = true;

    @Input()
    get enablePointerMoveEvents() { return this._enablePointerMoveEvents; }
    set enablePointerMoveEvents(value: boolean) {
        this._enablePointerMoveEvents = BooleanConverter(value);
    }
    private _enablePointerMoveEvents = true;

    @Input()
    get processDeepLinkOnLoad() { return this._processDeepLinkOnLoad; }
    set processDeepLinkOnLoad(value: boolean) {
        this._processDeepLinkOnLoad = BooleanConverter(value);
    }
    private _processDeepLinkOnLoad = true;

    @Input()
    get enableZoom() { return this._enableZoom; }
    set enableZoom(value: boolean) {
        this._enableZoom = BooleanConverter(value);
    }
    private _enableZoom = true;

    @Input()
    get showAttribution() { return this._showAttribution; }
    set showAttribution(value: boolean) {
        this._showAttribution = BooleanConverter(value);
    }
    private _showAttribution = true;

    @Input() get LayerService() {
        return this.layerService;
    }

    @Input() get MapService() {
        return this.mapService;
    }

    @Input() get AoiService() {
        return this.aoiService;
    }

    @Input() get DeepLinkService() {
        return this.deepLinkService;
    }

    @Input() get ScalebarService() {
        return this.scalebarService;
    }

    @Input() get ToSqlStringService() {
        return this.toSqlStringService;
    }

    @Input() get SpatialQueryService() {
        return this.spatialQueryService;
    }

    @Input() get UserSettingsService() {
        return this.userSettingsService;
    }

    @Input() get EsriInterceptor() {
        return Interceptor;
    }

    @Input()
    @InputConverter()
    zoom: number = defaultZoom;

    largerImageSubscription: Subscription;
    largerVideoSubscription: Subscription;
    @Output() readonly largerImageRequest: EventEmitter<MapCoreDocument[]> = new EventEmitter<MapCoreDocument[]>();
    @Output() readonly largerVideoRequest: EventEmitter<MapCoreDocument> = new EventEmitter<MapCoreDocument>();

    constructor(
        vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly componentService: ComponentService,
        private readonly mapApi: MapApi,
        private readonly layerService: LayerService,
        private readonly mapService: MapService,
        private readonly aoiService: AoiService,
        private readonly scalebarService: ScalebarService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly spatialQueryService: SpatialQueryService,
        private readonly deepLinkService: DeepLinkService,
        private readonly deepLinkProcessor: DeepLinkProcessor,
        private readonly queryStringParamService: QueryStringParamService,
        private readonly nearbyService: NearbyService,
        @Inject('Window') $window: Window,
        private readonly zone: NgZone,
        private readonly documentService: DocumentService,
        @Inject('UserSettingsService') private readonly userSettingsService: IUserSettingsService
    ) {
        super(vcr);
        this.$window = $window;
        this.largerImageSubscription = this.documentService.fullScreenImagesRequestedObservable.subscribe((documents) => this.onLargerImageRequested(documents));
        this.largerVideoSubscription = this.documentService.fullScreenVideoRequestedObservable.subscribe((document) => this.onLargerVideoRequested(document));
        this.registerEvent('largerImageRequest', this.largerImageRequest);

        window.addEventListener('showRouteAndSegmentMapLayersFromQueryParameters', () => {
            this.showRouteAndSegmentMapLayersFromQueryParameters();
        });
    }

    /** Reference to the displayed ESRI Map and MapView */
    @Input() map: NonNullable<IMCMap>;
    @Input() mapView: NonNullable<IMCMapView>;
    @Input() mapId: string;

    $window: Window;
    private clickHandler;
    private pointerMoveHandler;

    private readonly loadListeners = new Map<string, __esri.WatchHandle>();
    private readonly layerViewsChange = (event: __esri.CollectionChangeEvent<__esri.LayerView>) => {
        if (event && event.added && event.added.length) {
            for (const layerView of event.added) {
                this.loadListeners.set(layerView.layer.id, layerView.watch('updating', (current: boolean, _old: boolean, _prop: string, target: __esri.LayerView) => {
                    window.dispatchEvent(new CustomEvent<ILayerLoadStatusChangeDetail>('layerUpdatingChange', { detail: { layerId: target.layer.id, updating: current }, composed: true }));
                }));
            }
        }
    }
    private readonly layersChange = (event: __esri.CollectionChangeEvent<__esri.Layer>) => {
        if (event && event.removed && event.removed.length) {
            for (const layer of event.removed) {
                const handler = this.loadListeners.get(layer.id);
                if (handler) {
                    handler.remove();
                    this.loadListeners.delete(layer.id);
                }
            }
        }
    }

    async ngAfterViewInit() {
        const registerWithMcScopePromise = this.registerWithMcScope();
        try {
            // tell any parent mcScope that we exist.
            [MapConstructor, MapViewConstructor] = await Promise.all([ESRI.MCMap, ESRI.MCMapView, ESRI.Basemap, ESRI.WebTileLayer]);
            await this.zone.runOutsideAngular(async () => {
                if (this.enableZoom) {
                    this.defaultComponents.push('zoom');
                }

                if (this.showAttribution) {
                    this.defaultComponents.push('attribution');
                }

                this.map = MapConstructor({ basemap: this.basemap });
                this.mapView = await MapViewConstructor({
                    center: this.center,
                    container: this.mapAreaRef.nativeElement,
                    map: this.map,
                    ui: { components: this.defaultComponents },
                    zoom: this.zoom
                }, this.mapId, this.mapApi, this.layerService);
            });

            this.mapView.allLayerViews.on('change', this.layerViewsChange);
            this.map.allLayers.on('change', this.layersChange);

            //map constraints set after initialization because it doesn't seem to work
            //in the initialization, but works out here
            this.mapView.constraints = {
                rotationEnabled: this.rotate
            };
            this.map.watch('basemap', (newValue) => { this._basemap = newValue; });
            this.mapView.watch('center', (newValue) => { this.center = [newValue.longitude, newValue.latitude]; });
            this.mapView.watch('zoom', (newValue) => {
                this.zoom = newValue;
                this.mapView.map.allLayers.forEach((layer) => {
                    if (layer.type === 'feature') {
                        const featureLayer: IMapcoreFeatureLayer = layer as IMapcoreFeatureLayer;
                        if (featureLayer.mapcoreProps && featureLayer.mapcoreProps.heatmapMode && featureLayer.mapcoreProps.heatmapMode === HeatmapMode.Auto) {
                            const heatmapOn = this.layerService.checkIfHeatmapIsOnOrOff(layer);
                            if (heatmapOn) {
                                featureLayer.renderer = featureLayer.mapcoreProps.heatmapRenderer;
                            } else {
                                featureLayer.renderer = featureLayer.mapcoreProps.renderer;
                            }
                        }
                    }
                });
                this.changeDetectorRef.detectChanges();
            });

            this.mapService.mcMapView = this.mapView;
            this.layerService.mapView = this.mapView;
            this.aoiService.mapView = this.mapView;

            await this.setupWidgets();
            await registerWithMcScopePromise;
            // Process query string if present. Should be enhanced to process if MC values present.
            // work around for new geomap, which doesn't seem to properly set the extent from the config passed to it
            if (this.$window.location.search.indexOf('?') >= 0 && this.processDeepLinkOnLoad) {
                const seconds = 500;
                await setTimeout(<any>(async () => {
                    if (this.$window.location.search.indexOf('?') >= 0) {
                        //register mapcore deep link handlers last, so hosts had a chance to register first
                        this.mapView.when(() => {
                            this.deepLinkService.registerDeepLinkProcessors();
                            this.deepLinkProcessor.process();
                            window.dispatchEvent(new CustomEvent('openAoi', { detail: false, composed: true }));
                        });
                    }
                }), seconds);
            } else {
                window.dispatchEvent(new CustomEvent('openAoi', { detail: true, composed: true }));
            }
            await this.mapView.when();
            await this.activate();
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }
        finally {
            this.componentService.mapViewReady(this.mapView);
            this.isReady();
        }
    }

    @Input()
    public disposeMap = async () => {
        await this.unregisterWithMcScope();
    }

    private async setupWidgets() {
        const slots = [
            { key: 'bottom-left', element: this.slotBottomLeft },
            { key: 'bottom-right', element: this.slotBottomRight },
            { key: 'top-left', element: this.slotTopLeft },
            { key: 'top-right', element: this.slotTopRight }
        ];
        for (const slot of slots) {
            if (slot && slot.element.nativeElement.assignedNodes().length > 0) {
                this.mapView.ui.add(slot.element.nativeElement, slot.key);
            }
        }
    }

    @Input()
    public async setBasemapsFromConfig(basemapConfig: IBasemapConfig) {
        await this.LayerService.getLayerCopyRight(basemapConfig);
        this.basemap = await CreateBasemapsFromConfig(basemapConfig);
    }

    @Input()
    public zoomToLayer = async (layerId: string, zoomThreshold: number = 14) => {
        await this.layerService.zoomToLayer(layerId, zoomThreshold);
    }

    @Input()
    public activate = async () => {
        // Unable to access this.mapView from within handler
        const mapView = this.mapView;

        if (!this.pointerMoveHandler && this.enablePointerMoveEvents) {
            this.pointerMoveHandler = mapView.on('pointer-move', function(event) {
                mapView.hitTest(event)
                    .then(function(response) {
                        let cursorStyle = 'default';

                        if (response.results.filter(r => r.graphic.layer && r.graphic.layer.type === 'graphics').length > 0) {
                            cursorStyle = 'pointer';
                        }

                        const map = document.getElementsByTagName('mc-map');

                        if (map !== null && map.length === 1) {
                            map[0].style.cursor = cursorStyle;
                        }
                    });
            });
        }

        if (!this.clickHandler && this.enableClickEvents) {
            // Unable to access this.layerService from within handler
            const layerService = this.layerService;

            this.clickHandler = mapView.on('click', async (event: __esri.ViewClickEvent) => {
                window.dispatchEvent(new CustomEvent('mapUpdateBegin', { detail: '' }));

                mapView.hitTest(event)
                    .then(async function(response) {
                        if (response.results.filter(r => r.graphic.layer.type === 'graphics').length > 0) {
                            const segmentKeyName = 'SEG_NO';
                            const retryCountMax = 5;
                            let retryCount = 0;
                            let continueSegmentSearch = true;
                            let performAdditionalSearch = false;
                            let additionalSearchDistanceInFeet = 0;
                            const searchDistanceAdjustment = Math.max(4, layerService.getTolerance(1));
                            const searchDistanceAdjustmentIncrease = Math.max(4, searchDistanceAdjustment * 0.2);
                            const searchDistanceAdjustmentDecrease = Math.max(4, searchDistanceAdjustment * 0.1);
                            const currentSegment = layerService.selectedLayer == null
                                ? ''
                                : layerService.selectedLayer.title.split('|')[2];

                            while ((continueSegmentSearch && retryCount < retryCountMax) || performAdditionalSearch) {
                                let results = (await layerService.identifyMapImagelayers(event.mapPoint, false, false, 1, additionalSearchDistanceInFeet)).features;

                                performAdditionalSearch = false;

                                // If no results, then increase search distance
                                // Else if segment(s) found that is current segment, then done searching (may find multiple that represent same segment)
                                // Else if segment(s) found that is not current segment but it same segment, then done searching and make new selection (may find multiple that represent same segment)
                                // Otherwise, combination of current segment and other segments so decrease search distance to narrow results
                                if (results.length === 0) {
                                    retryCount++;

                                    // If not yet reached maximum number of searches
                                    // Else if reached maximum number of searches, then do one final search with much larger search distance
                                    if (retryCount < retryCountMax) {
                                        additionalSearchDistanceInFeet += searchDistanceAdjustmentIncrease;
                                    } else if (retryCount === retryCountMax) {
                                        additionalSearchDistanceInFeet = additionalSearchDistanceInFeet * 2;
                                        performAdditionalSearch = true;
                                    }
                                } else if (results.filter(r => r.attributes[segmentKeyName] !== currentSegment).length === 0) {
                                    continueSegmentSearch = false;
                                } else if (results.every(r => r.attributes[segmentKeyName] === results[0].attributes[segmentKeyName])) {
                                    continueSegmentSearch = false;
                                    window.dispatchEvent(new CustomEvent('selectSegment', { detail: results[0].attributes[segmentKeyName] }));
                                } else {
                                    additionalSearchDistanceInFeet -= searchDistanceAdjustmentDecrease;
                                    retryCount++;

                                    // If reached maxiumum number of searches or completed additional search
                                    if (retryCount >= retryCountMax) {
                                        // Remove result(s) that match selected segment
                                        results = results.filter(r => r.attributes[segmentKeyName] !== currentSegment);

                                        // Select first result; this is ok because this scenario should only happen if very zoomed out and precision will be lacking
                                        window.dispatchEvent(new CustomEvent('selectSegment', { detail: results[0].attributes[segmentKeyName] }));
                                    }
                                }
                            }
                        }

                        window.dispatchEvent(new CustomEvent('mapUpdateEnd', { detail: '' }));
                    });
            });
        }
    }

    private async showRouteAndSegmentMapLayersFromQueryParameters() {
        window.dispatchEvent(new CustomEvent('mapUpdateBegin', { detail: '' }));

        // Wait for map to load
        while (!this.mapView) {
            await util.sleep(10);
        }

        let routeLayer: IMapcoreSubLayer;
        const queryStringParameters = this.queryStringParamService.getQueryStringParams();

        if (this.deepLinkService.deepLinkParamIsPresent('segmentNumber')) {
            const segmentNumberParameterKeyName = 'segmentNumber';
            const searchCriteriaParametersWithSegment = this.deepLinkProcessor.getParameters(this.deepLinkService.parameters, queryStringParameters);
            ((this.deepLinkService.parameters[segmentNumberParameterKeyName]) as Array<string>).forEach(p => {
                delete queryStringParameters[p];  //tslint:disable-line
            });
            const searchCriteriaParametersWithoutSegment = this.deepLinkProcessor.getParameters(this.deepLinkService.parameters, queryStringParameters);

            await this.setSearchCriteriaFromQueryParameters(searchCriteriaParametersWithoutSegment);
            routeLayer = await this.aoiService.showLayersFromQueryParameters(true, false, 'rgba(255, 128, 0, 1)');

            await this.setSearchCriteriaFromQueryParameters(searchCriteriaParametersWithSegment);
            this.layerService.selectedLayer = await this.aoiService.showLayersFromQueryParameters(false, true, 'rgba(255, 0, 0, 1)');
        } else {
            const searchCriteriaParameters = this.deepLinkProcessor.getParameters(this.deepLinkService.parameters, queryStringParameters);
            await this.setSearchCriteriaFromQueryParameters(searchCriteriaParameters);
            routeLayer = await this.aoiService.showLayersFromQueryParameters(true, true, 'rgba(255, 128, 0, 1)');
            this.layerService.selectedLayer = null;
        }

        const routeAndSegmentBufferTitle = 'Route and Segment Buffer';

        this.layerService.mapLayers.graphics.forEach(layer => {
            if (layer.title === routeAndSegmentBufferTitle) {
                layer.removeAll();
            }
        });

        let routeLinePoints = [];
        let segmentLinePoints = [];
        [routeLinePoints, segmentLinePoints] = await Promise.all([
            this.getLinePointsAndCreateLineBuffer(routeLayer, routeAndSegmentBufferTitle, this.layerService.selectedLayer == null, true),
            this.getLinePointsAndCreateLineBuffer(this.layerService.selectedLayer, routeAndSegmentBufferTitle, this.layerService.selectedLayer != null, false)
        ]);
        const linePoints = this.layerService.selectedLayer !== null ? segmentLinePoints : routeLinePoints;

        let startX = linePoints[0][0];
        let startY = linePoints[0][1];
        let endX = linePoints[linePoints.length - 1][0];
        let endY = linePoints[linePoints.length - 1][1];
        linePoints.forEach(p => {
            if (p[0] < startX) {
                startX = p[0];
                startY = p[1];
            } else if (p[0] > endX) {
                endX = p[0];
                endY = p[1];
            }
        });

        const dy = endY - startY;
        const dx = endX - startX;
        let theta = Math.atan2(dy, dx);
        theta *= 180 / Math.PI;
        if (theta < 0) {
            theta = 360 + theta;
        }

        // Need to enable rotation in order to update it; restore to configured value
        this.mapView.constraints.rotationEnabled = true;
        this.mapView.rotation = theta;
        this.mapView.constraints.rotationEnabled = this._rotate;

        window.dispatchEvent(new CustomEvent('mapUpdateEnd', { detail: '' }));
    }

    private async setSearchCriteriaFromQueryParameters(searchCriteria) {
        this.aoiService.setSearchCriterias([]);
        const currentSearchCriterias: any = this.aoiService.getSearchCriterias();
        const searchCriterias: ISearchCriteria[] = await this.deepLinkService.getSearchCriterias(currentSearchCriterias, searchCriteria.parameters);
        this.aoiService.setSearchCriterias(searchCriterias);
    }

    private async getLinePointsAndCreateLineBuffer(layer, routeAndSegmentBufferTitle, getLinePoints, createBuffer) {
        const linePoints = [];

        if (layer != null) {
            const queryFeaturesResponse = await layer.queryFeatures();

            queryFeaturesResponse.features.forEach(async feature => {
                if (getLinePoints) {
                    (feature.geometry as __esri.Polyline).paths[0].forEach(p => {
                        linePoints.push(p);
                    });
                }

                if (createBuffer) {
                    const buffers = await this.nearbyService.getBuffers(25, 'feet', [feature.geometry]);

                    const buffer = buffers.map(b => b.graphic);
                    buffer.forEach(b => {
                        // Note: cannot completely hide graphic buffer or it is not detected at all on screen
                        b.symbol.color.a = 0;
                        const outlineColor = (b.symbol as any).outline.color;
                        outlineColor.r = 255;
                        outlineColor.g = 255;
                        outlineColor.b = 255;
                        outlineColor.a = 0.01;
                    });

                    await this.nearbyService.setNearbyLayer(routeAndSegmentBufferTitle, buffer, false);
                }
            });
        }

        return linePoints;
    }

    onLargerImageRequested(documents: MapCoreDocument[]) {
        this.largerImageRequest.emit(documents);
    }
    onLargerVideoRequested(documents: MapCoreDocument) {
        this.largerVideoRequest.emit(documents);
    }
}

export type DefaultMapComponent = 'attribution' | 'zoom';
